import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSidebarItems, flattenSidebarItemsForSearch } from './config/SidebarItems';
import { useAuth } from '../../contexts/AuthContext';
import { useModuleEntitlements } from '../../modules/module-management';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { search, SearchResponse } from '../../services/searchService';

type CombinedItem =
  | { kind: 'page'; path: string; label: string; group?: string; icon?: React.ReactNode }
  | { kind: 'record'; path: string; label: string; subtitle?: string; typeLabel: string };

// Flatten SearchResponse to array of record items for unified list
function flattenRecordResults(results: SearchResponse): CombinedItem[] {
  const items: CombinedItem[] = [];
  for (const module of Object.keys(results)) {
    const result = results[module];
    if (!result?.items?.length) continue;
    for (const item of result.items) {
      items.push({
        kind: 'record',
        path: item.link,
        label: item.title,
        subtitle: item.subtitle,
        typeLabel: item.type,
      });
    }
  }
  return items;
}

const recordTypeIcons: Record<string, React.ReactNode> = {
  Client: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  ),
  Task: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
    </svg>
  ),
  Lead: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
    </svg>
  ),
  Invoice: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  ),
  Todo: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
    </svg>
  ),
  Note: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
    </svg>
  ),
  Appointment: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  ),
  Call: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
    </svg>
  ),
};
const defaultRecordIcon = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

const CombinedSearch: React.FC = () => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [recordResults, setRecordResults] = useState<SearchResponse>({});
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [lastSearchedQuery, setLastSearchedQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { colors } = useTheme();
  const { user, can, permissionsLoaded } = useAuth();
  const { entitlements } = useModuleEntitlements();

  const canView = useMemo(
    () => (permissionsLoaded ? (moduleKey: string) => can(moduleKey, 'view') : undefined),
    [can, permissionsLoaded]
  );

  const flatItems = useMemo(() => {
    const items = getSidebarItems(user?.role, entitlements, canView);
    return flattenSidebarItemsForSearch(items);
  }, [user?.role, entitlements, canView]);

  const filteredPages = useMemo(() => {
    if (!query.trim()) return flatItems.slice(0, 8);
    const q = query.trim().toLowerCase();
    return flatItems.filter(
      (e) => e.label.toLowerCase().includes(q) || (e.group && e.group.toLowerCase().includes(q))
    );
  }, [flatItems, query]);

  // Run record search only on Enter (triggered in handleKeyDown)
  const runRecordSearch = useCallback(() => {
    const q = query.trim();
    if (!q) return;
    setRecordsLoading(true);
    setLastSearchedQuery(q);
    search(q, undefined, 8)
      .then((data) => {
        setRecordResults(data);
        setRecordsLoading(false);
      })
      .catch(() => {
        setRecordResults({});
        setRecordsLoading(false);
      });
  }, [query]);

  // Clear record results only when the user changes the query after having searched (avoids setState on every keystroke)
  useEffect(() => {
    if (lastSearchedQuery === '') return;
    if (query.trim() === lastSearchedQuery) return;
    setRecordResults({});
    setLastSearchedQuery('');
  }, [query, lastSearchedQuery]);

  const recordItems = useMemo(() => flattenRecordResults(recordResults), [recordResults]);

  const combinedList = useMemo<CombinedItem[]>(() => {
    const pages: CombinedItem[] = filteredPages.map((p) => ({
      kind: 'page' as const,
      path: p.path,
      label: p.label,
      group: p.group,
      icon: p.icon,
    }));
    return [...pages, ...recordItems];
  }, [filteredPages, recordItems]);

  const selectPath = useCallback(
    (path: string) => {
      navigate(path);
      setQuery('');
      setOpen(false);
      setHighlightIndex(0);
    },
    [navigate]
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const q = query.trim();
      // If we already have results for this query, select the highlighted item
      if (open && combinedList.length > 0 && lastSearchedQuery === q) {
        const item = combinedList[highlightIndex];
        if (item) selectPath(item.path);
        return;
      }
      // Otherwise run record search (pages still show from incremental filter)
      if (q) {
        runRecordSearch();
        setOpen(true);
        setHighlightIndex(0);
      }
      return;
    }
    if (!open) return;
    if (combinedList.length === 0) {
      if (e.key === 'Escape') setOpen(false);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((i) => (i + 1) % combinedList.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((i) => (i - 1 + combinedList.length) % combinedList.length);
    } else if (e.key === 'Escape') {
      setOpen(false);
      setHighlightIndex(0);
    }
  };

  useEffect(() => {
    setHighlightIndex(0);
    setOpen(query.length > 0);
  }, [query]);

  useEffect(() => {
    if (open && listRef.current && combinedList.length) {
      const el = listRef.current.querySelector(`[data-index="${highlightIndex}"]`);
      el?.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightIndex, open, combinedList.length]);

  useEffect(() => {
    const handleClickOutside = (ev: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(ev.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
        if (query.length > 0) setHighlightIndex(0);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [query.length]);

  const hasPages = filteredPages.length > 0;
  const hasRecords = recordItems.length > 0;
  const hasSearchedRecords = lastSearchedQuery !== '';
  const showRecordsSection = query.trim() && (recordsLoading || hasRecords);
  const showEnterHint = query.trim() && !hasSearchedRecords && !recordsLoading;
  const showDropdown = open && (hasPages || showRecordsSection || showEnterHint || query.trim().length > 0);

  return (
    <div ref={containerRef} className="relative w-full max-w-xl">
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400 dark:text-gray-500">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </span>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => query.length > 0 && setOpen(true)}
          placeholder="Search pages and records…"
          className={cn(
            'w-full pl-9 py-2 rounded-lg text-sm border transition-colors',
            query.length > 0 ? 'pr-9' : 'pr-4',
            colors.surface,
            colors.border,
            colors.text,
            'placeholder:text-gray-400 dark:placeholder:text-gray-500',
            'focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent'
          )}
          aria-label="Search pages and records"
          aria-expanded={open}
          aria-autocomplete="list"
          role="combobox"
        />
        {query.length > 0 && (
          <button
            type="button"
            onClick={() => {
              setQuery('');
              setOpen(false);
              setHighlightIndex(0);
              inputRef.current?.focus();
            }}
            className={cn(
              'absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md transition-colors',
              colors.textMuted,
              'hover:bg-black/5 dark:hover:bg-white/5 hover:text-gray-700 dark:hover:text-gray-200',
              'focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1'
            )}
            aria-label="Clear search"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>
      {showDropdown && (
        <div
          ref={listRef}
          role="listbox"
          className={cn(
            'absolute left-0 right-0 top-full mt-1 max-h-80 overflow-auto rounded-lg border shadow-lg z-50',
            colors.surface,
            colors.border
          )}
        >
          {combinedList.length === 0 && !recordsLoading ? (
            <div className={cn('px-4 py-3 text-sm', colors.textMuted)}>
              {query.trim()
                ? (showEnterHint ? 'Press Enter to search records.' : 'No pages or records match.')
                : 'Type to search pages and records.'}
            </div>
          ) : (
            <>
              {hasPages && (
                <div className={cn('px-3 pt-2 pb-1', colors.border, 'border-b')}>
                  <p className={cn('text-[11px] font-semibold uppercase tracking-wider', colors.textMuted)}>Pages</p>
                </div>
              )}
              {combinedList.map((item, index) => {
                const isFirstRecord = item.kind === 'record' && combinedList.findIndex((x) => x.kind === 'record') === index;
                if (item.kind === 'page') {
                  return (
                    <button
                      key={`page-${item.path}-${index}`}
                      type="button"
                      role="option"
                      data-index={index}
                      aria-selected={index === highlightIndex}
                      onClick={() => selectPath(item.path)}
                      onMouseEnter={() => setHighlightIndex(index)}
                      className={cn(
                        'w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors',
                        index === highlightIndex
                          ? 'bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-200'
                          : colors.text
                      )}
                    >
                      {item.icon ? (
                        <span className={cn('flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg', index === highlightIndex ? 'bg-indigo-200/50 dark:bg-indigo-500/30' : 'bg-black/5 dark:bg-white/5')}>
                          {item.icon}
                        </span>
                      ) : (
                        <span className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg bg-black/5 dark:bg-white/5">
                          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                          </svg>
                        </span>
                      )}
                      <span className="flex-1 truncate font-medium">{item.label}</span>
                      {item.group && (
                        <span className={cn('text-xs truncate shrink-0', colors.textMuted)}>{item.group}</span>
                      )}
                    </button>
                  );
                }
                return (
                  <React.Fragment key={`record-${item.path}-${index}`}>
                    {isFirstRecord && (
                      <div className={cn('px-3 pt-2 pb-1', colors.border, 'border-t')}>
                        <p className={cn('text-[11px] font-semibold uppercase tracking-wider', colors.textMuted)}>Records</p>
                      </div>
                    )}
                  <button
                    type="button"
                    role="option"
                    data-index={index}
                    aria-selected={index === highlightIndex}
                    onClick={() => selectPath(item.path)}
                    onMouseEnter={() => setHighlightIndex(index)}
                    className={cn(
                      'w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors border-t first:border-t-0',
                      colors.borderLight,
                      index === highlightIndex
                        ? 'bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-200'
                        : colors.text
                    )}
                  >
                    <span className={cn('flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg', colors.textMuted)}>
                      {recordTypeIcons[item.typeLabel] ?? defaultRecordIcon}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="truncate font-medium">{item.label}</p>
                      {item.subtitle && (
                        <p className={cn('text-xs truncate', colors.textMuted)}>{item.subtitle}</p>
                      )}
                      <p className={cn('text-[11px]', colors.textMuted)}>{item.typeLabel}</p>
                    </div>
                  </button>
                  </React.Fragment>
                );
              })}
              {recordsLoading && (
                <div className={cn('px-4 py-2 text-xs', colors.textMuted, 'border-t', colors.borderLight)}>
                  Searching records…
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default CombinedSearch;
